import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FeedbackBanner from '../../components/feature/FeedbackBanner';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';

export default function ReformaTributariaConsumoArticle() {
  const article = {
    title: 'Reforma Tributária do Consumo: o que muda e por que ela é considerada histórica?',
    description: 'A maior transformação do sistema tributário brasileiro desde 1988. Entenda os pilares, impactos e mudanças que afetam empresas e consumidores.',
    author: 'Equipe Ativo News',
    date: '2025-01-15',
    category: 'Reforma Tributária',
    readTime: '12 min',
    image: 'https://public.readdy.ai/ai/img_res/629a739a635587971da5cc1beac97bca.jpg'
  };

  const relatedArticles = [
    {
      title: 'Reforma Tributária: Impactos no Lucro Presumido e Lucro Real',
      slug: 'reforma-tributaria-lucro-presumido-real',
      image: 'https://readdy.ai/api/search-image?query=corporate%20tax%20planning%20concept%20with%20financial%20charts%20clean%20professional%20background&width=400&height=250&seq=reforma-lucro-related&orientation=landscape',
      category: 'Reforma Tributária'
    },
    {
      title: 'Fim do CIAP do PIS/COFINS com a Reforma Tributária?',
      slug: 'fim-ciap-pis-cofins-reforma-tributaria',
      image: 'https://readdy.ai/api/search-image?query=tax%20credit%20management%20system%20concept%20clean%20professional%20background&width=400&height=250&seq=ciap-related&orientation=landscape',
      category: 'Reforma Tributária'
    },
    {
      title: 'Planejamento Tributário: Como Reduzir Custos Legalmente',
      slug: 'planejamento-tributario',
      image: 'https://readdy.ai/api/search-image?query=strategic%20tax%20planning%20concept%20with%20financial%20optimization%20clean%20professional%20background&width=400&height=250&seq=planejamento-related&orientation=landscape',
      category: 'Planejamento Tributário'
    }
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: article.author
    }
  };

  return (
    <>
      <SEOHead
        title="Reforma Tributária do Consumo: o que muda e por que é histórica | Ativo News"
        description="A maior transformação do sistema tributário brasileiro desde 1988. Entenda os pilares, impactos e mudanças que afetam empresas e consumidores."
        keywords="reforma tributária, reforma tributária do consumo, CBS, IBS, simplificação tributária, sistema tributário brasileiro"
        canonicalUrl="https://ativonews.com.br/artigo/reforma-tributaria-consumo"
      />
      <SchemaOrg data={schemaData} />
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main className="flex-grow">
          {/* Hero Section */}
          <div className="relative h-[500px] bg-gray-900">
            <img 
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
            <div className="absolute inset-0 flex items-end">
              <div className="max-w-4xl mx-auto px-6 pb-16 w-full">
                <span className="inline-block px-3 py-1 bg-[#CC0000] text-white text-xs font-bold uppercase mb-4">
                  {article.category}
                </span>
                <h1 className="text-5xl font-bold text-white leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-6 text-white/80 text-sm">
                  <span className="flex items-center gap-2">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-2">
                    <i className="ri-calendar-line"></i>
                    {new Date(article.date).toLocaleDateString('pt-BR')}
                  </span>
                  <span className="flex items-center gap-2">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Article Content */}
          <article className="max-w-4xl mx-auto px-6 py-16">
            <div className="prose prose-lg max-w-none">
              
              {/* Introdução */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Introdução</h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  A <strong>Reforma Tributária do consumo</strong> representa a maior transformação do sistema tributário brasileiro desde a Constituição de 1988. Seu objetivo é simplificar, tornar mais justo e mais eficiente o modelo de arrecação sobre bens e serviços.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Esta mudança histórica promete revolucionar a forma como empresas e consumidores lidam com tributos no Brasil, trazendo mais transparência, previsibilidade e eficiência para o sistema econômico nacional.
                </p>
              </section>

              {/* Principais Objetivos */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  <i className="ri-question-line text-[#CC0000] mr-3"></i>
                  Quais são os principais objetivos da Reforma Tributária?
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  A reforma tem três pilares centrais que norteiam todas as mudanças propostas:
                </p>
                
                <div className="grid gap-6 mb-8">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg border-l-4 border-blue-600">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                      <i className="ri-line-chart-line text-blue-600 text-2xl"></i>
                      1. Estimular o crescimento econômico sustentável
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Redução da burocracia e dos custos de conformidade tributária, permitindo que empresas invistam mais em produção, inovação e expansão de negócios.
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-lg border-l-4 border-green-600">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                      <i className="ri-scales-line text-green-600 text-2xl"></i>
                      2. Reduzir desigualdades sociais e regionais
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Eliminação da guerra fiscal entre estados e criação de mecanismos de redistribuição mais equitativos, beneficiando regiões menos desenvolvidas.
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-lg border-l-4 border-purple-600">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                      <i className="ri-file-list-3-line text-purple-600 text-2xl"></i>
                      3. Simplificar o sistema tributário e aumentar a transparência
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Unificação de tributos e criação de um sistema mais claro, onde empresas e consumidores possam entender exatamente quanto pagam de impostos.
                    </p>
                  </div>
                </div>
              </section>

              {/* Sistema Atual Ineficiente */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  <i className="ri-question-line text-[#CC0000] mr-3"></i>
                  Por que o sistema atual é considerado ineficiente?
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  O modelo tributário brasileiro sobre consumo apresenta diversos problemas estruturais que prejudicam a economia:
                </p>

                <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-lg mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                    Principais Problemas do Sistema Atual
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Cumulatividade:</strong>
                        <span className="text-gray-700"> Tributos incidem sobre tributos, gerando efeito cascata que encarece produtos e serviços</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Complexidade extrema:</strong>
                        <span className="text-gray-700"> Múltiplos tributos (ICMS, ISS, PIS, COFINS, IPI) com regras diferentes em cada estado e município</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Litígios excessivos:</strong>
                        <span className="text-gray-700"> Interpretações divergentes geram milhares de processos judiciais, travando recursos e criando insegurança jurídica</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Guerra fiscal:</strong>
                        <span className="text-gray-700"> Estados competem oferecendo benefícios fiscais irregulares, prejudicando a arrecadação e distorcendo a concorrência</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Falta de transparência:</strong>
                        <span className="text-gray-700"> Consumidores não conseguem identificar quanto pagam de imposto nos produtos, dificultando escolhas conscientes</span>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">💡 Impacto prático:</strong> Estima-se que empresas brasileiras gastem cerca de 1.500 horas por ano apenas para calcular e pagar tributos, um dos maiores índices do mundo segundo o Banco Mundial.
                  </p>
                </div>
              </section>

              {/* Impactos */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  <i className="ri-question-line text-[#CC0000] mr-3"></i>
                  Como a reforma impacta empresas e consumidores?
                </h2>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {/* Impactos para Empresas */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-xl">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <i className="ri-building-line text-white text-2xl"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">Para Empresas</h3>
                    </div>
                    
                    <ul className="space-y-4">
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl font-bold mt-1"></i>
                        <div>
                          <strong className="text-gray-900">Redução de custos operacionais:</strong>
                          <p className="text-gray-700 text-sm mt-1">Menos tempo e recursos gastos com compliance tributário</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl font-bold mt-1"></i>
                        <div>
                          <strong className="text-gray-900">Segurança jurídica:</strong>
                          <p className="text-gray-700 text-sm mt-1">Regras mais claras reduzem riscos de autuações e litígios</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl font-bold mt-1"></i>
                        <div>
                          <strong className="text-gray-900">Competitividade:</strong>
                          <p className="text-gray-700 text-sm mt-1">Fim da guerra fiscal cria ambiente de concorrência mais justa</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl font-bold mt-1"></i>
                        <div>
                          <strong className="text-gray-900">Crédito tributário:</strong>
                          <p className="text-gray-700 text-sm mt-1">Sistema não-cumulativo permite aproveitamento integral de créditos</p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Impactos para Consumidores */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-xl">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-white text-2xl"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">Para Consumidores</h3>
                    </div>
                    
                    <ul className="space-y-4">
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl font-bold mt-1"></i>
                        <div>
                          <strong className="text-gray-900">Transparência total:</strong>
                          <p className="text-gray-700 text-sm mt-1">Visualização clara do valor de imposto em cada produto</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl font-bold mt-1"></i>
                        <div>
                          <strong className="text-gray-900">Preços mais eficientes:</strong>
                          <p className="text-gray-700 text-sm mt-1">Redução do efeito cascata tende a baixar preços no médio prazo</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl font-bold mt-1"></i>
                        <div>
                          <strong className="text-gray-900">Escolhas conscientes:</strong>
                          <p className="text-gray-700 text-sm mt-1">Informação clara permite decisões de compra mais informadas</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-green-600 text-xl font-bold mt-1"></i>
                        <div>
                          <strong className="text-gray-900">Benefícios sociais:</strong>
                          <p className="text-gray-700 text-sm mt-1">Cesta básica e itens essenciais podem ter alíquotas reduzidas</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-teal-50 to-teal-100 p-8 rounded-xl border-l-4 border-teal-600">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <i className="ri-lightbulb-line text-teal-600 text-2xl"></i>
                    Visão de Longo Prazo
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    No médio e longo prazo, a reforma tende a gerar um ciclo virtuoso: empresas mais eficientes → preços mais competitivos → maior consumo → mais investimentos → crescimento econômico → geração de empregos.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Estudos indicam que a simplificação tributária pode aumentar o PIB brasileiro em até 12% nas próximas décadas, representando ganhos significativos para toda a sociedade.
                  </p>
                </div>
              </section>

              {/* Principais Mudanças */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Principais Mudanças Estruturais</h2>
                
                <div className="space-y-6">
                  <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-[#CC0000] transition-colors">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                      <span className="w-8 h-8 bg-[#CC0000] text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                      Criação do IBS (Imposto sobre Bens e Serviços)
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Substitui ICMS e ISS, unificando a tributação estadual e municipal em um único tributo com regras nacionais uniformes.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-[#CC0000] transition-colors">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                      <span className="w-8 h-8 bg-[#CC0000] text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                      Criação da CBS (Contribuição sobre Bens e Serviços)
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Substitui PIS e COFINS, simplificando a tributação federal sobre o consumo.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-[#CC0000] transition-colors">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                      <span className="w-8 h-8 bg-[#CC0000] text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                      Modelo Não-Cumulativo Pleno
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Elimina o efeito cascata, permitindo que empresas aproveitem créditos tributários em toda a cadeia produtiva.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-[#CC0000] transition-colors">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                      <span className="w-8 h-8 bg-[#CC0000] text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                      Tributação no Destino
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Imposto é recolhido no estado/município de consumo, não de produção, acabando com a guerra fiscal.
                    </p>
                  </div>
                </div>
              </section>

              {/* Cronograma */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Cronograma de Implementação</h2>
                
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-xl">
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                          2026
                        </div>
                        <div className="w-1 h-full bg-blue-300 mt-2"></div>
                      </div>
                      <div className="flex-1 pb-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Fase de Testes</h3>
                        <p className="text-gray-700">Início da aplicação experimental com alíquota de 0,1% para CBS e IBS</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">
                          2027
                        </div>
                        <div className="w-1 h-full bg-green-300 mt-2"></div>
                      </div>
                      <div className="flex-1 pb-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Início da Transição</h3>
                        <p className="text-gray-700">CBS e IBS começam a vigorar com alíquotas crescentes, convivendo com tributos antigos</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                          2027-32
                        </div>
                        <div className="w-1 h-full bg-purple-300 mt-2"></div>
                      </div>
                      <div className="flex-1 pb-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Período de Transição</h3>
                        <p className="text-gray-700">Redução gradual dos tributos antigos e aumento proporcional dos novos</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-[#CC0000] rounded-full flex items-center justify-center text-white font-bold">
                          2033
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Implementação Completa</h3>
                        <p className="text-gray-700">Extinção total de ICMS, ISS, PIS e COFINS. Vigência plena de CBS e IBS</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Conclusão */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusão</h2>
                <div className="bg-gradient-to-r from-[#CC0000]/10 to-[#CC0000]/5 p-8 rounded-xl border-l-4 border-[#CC0000]">
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    A Reforma Tributária do Consumo é, sem dúvida, uma das mudanças mais significativas da história econômica brasileira. Sua implementação representa um desafio técnico e político imenso, mas os benefícios potenciais justificam o esforço.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    Para empresas, o momento é de preparação: entender as novas regras, adaptar sistemas, treinar equipes e planejar a transição. Para consumidores, é uma oportunidade de exercer cidadania fiscal com mais consciência.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed font-semibold">
                    O sucesso da reforma dependerá da capacidade de todos os atores — governo, empresas e sociedade — de colaborarem para construir um sistema tributário mais justo, eficiente e transparente.
                  </p>
                </div>
              </section>

              {/* CTA */}
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Precisa de ajuda para se preparar para a Reforma Tributária?
                </h3>
                <p className="text-white/80 mb-6">
                  Nossa equipe de especialistas pode ajudar sua empresa a navegar pelas mudanças
                </p>
                <Link 
                  to="/contato"
                  className="inline-block px-8 py-3 bg-[#CC0000] text-white font-semibold rounded-lg hover:bg-[#AA0000] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Fale com Especialista
                </Link>
              </div>

            </div>
          </article>

          {/* Related Articles */}
          <section className="bg-gray-50 py-16">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Artigos Relacionados</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedArticles.map((article, index) => (
                  <Link 
                    key={index}
                    to={`/artigo/${article.slug}`}
                    className="group bg-white overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <span className="text-xs font-bold text-[#CC0000] uppercase mb-2 block">
                        {article.category}
                      </span>
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#CC0000] transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Newsletter */}
          <section className="bg-white py-16">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Receba as últimas notícias sobre Reforma Tributária
              </h2>
              <p className="text-gray-600 mb-8">
                Assine nossa newsletter e fique por dentro de todas as mudanças
              </p>
              <form className="flex gap-4 max-w-md mx-auto" data-readdy-form id="newsletter-reforma-tributaria">
                <input 
                  type="email"
                  name="email"
                  placeholder="Seu melhor e-mail"
                  required
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#CC0000] text-sm"
                />
                <button 
                  type="submit"
                  className="px-6 py-3 bg-[#CC0000] text-white font-semibold rounded-lg hover:bg-[#AA0000] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Assinar
                </button>
              </form>
            </div>
          </section>

          <FeedbackBanner />
        </main>

        <Footer />
      </div>
    </>
  );
}
